/* eslint-disable no-magic-numbers */
import { ImageIcon } from '../display/ImageIcon';
import { InfoPane } from '../display/InfoPane';
import { Label } from '../display/Label';
import { Radial } from '../display/Radial';
import { TextIcon } from '../display/TextIcon';
import { TextLabel } from '../display/TextLabel';
import {
  BuilderDependencies,
  ModuleTagging,
  RadialLayout,
  StrictNodeModel,
} from '../types';

import { resolveFilterPath } from './resolveFilterPath';

export function buildChildrenFromJSON(
  parentRadial: Radial,
  childArray: readonly StrictNodeModel[],
  opts: {
    filtered?: boolean;
    layout?: RadialLayout;
    preview?: boolean;
    fontFamily: string;
    moduleTagging?: readonly ModuleTagging[];
  },
  deps: BuilderDependencies,
) {
  const hideLabels = opts.layout == 'horizontal' || opts.layout == 'vertical';

  const displayCount = childArray.filter((c) => !c.hidden).length;
  let displayIndex = 0;

  for (const c of childArray) {
    const { angle, stemLength, size } = c.hidden
      ? { angle: 0, stemLength: 0, size: 0 }
      : getNodeDisplayParameters({
          index: displayIndex,
          count: displayCount,
          layout: opts.layout,
        });

    if (!c.hidden) {
      displayIndex++;
    }

    const radial = parentRadial.addRadial({
      angle,
      stemLength,
      size,
      color: c.color,
      icon:
        c.icon.image !== undefined
          ? new ImageIcon(c.icon.image)
          : new TextIcon(c.icon.text, { fontFamily: opts.fontFamily }),
      tag: c.tag,
      key: c.key,
      ariaLabel: c.ariaLabel,
      alias: c.alias,
      href: deps.getHref(c.href, opts.preview),
      moduleSlug: c.moduleSlug,
      dataPath: c.dataPath,
      dataTarget: c.dataTarget,
      hidden: c.hidden,
      hideProgressBar: c.hideProgressBar,
      recursiveLogic: c.recursiveLogic,
    });

    if (c.label && !hideLabels) {
      if (typeof c.label === 'string') {
        radial.setLabel(
          new TextLabel({ text: c.label, angle, fontFamily: opts.fontFamily }),
        );
      } else if (c.label.class) {
        radial.setLabel(new Label(deps.lib, c.label.class, c.label.frame));
      }
    }

    switch (c.layout) {
      case 'horizontal':
        c.centerOffset = c.centerOffset ?? { x: 0, y: -150 };
        break;
      case 'modes':
        c.centerOffset = c.centerOffset ?? { x: 100, y: -25 };
        break;
      case 'operations':
        c.centerOffset = c.centerOffset ?? { x: 50, y: -50 };
        break;
      case 'skill-set':
        c.centerOffset = c.centerOffset ?? { x: -150, y: -30 };
        break;
      case 'vertical':
        c.centerOffset = c.centerOffset ?? { x: -150, y: 0 };
        break;
    }

    if (c.centerOffset) {
      radial.setPosition(c.centerOffset.x, c.centerOffset.y);
    }

    // Resolve filterTagPath to get children dynamically
    const resolvedChildren =
      c.filterTagPath && c.filterRoot && opts.moduleTagging
        ? resolveFilterPath(c, opts.moduleTagging)
        : c.children;

    if (resolvedChildren.length) {
      if (c.info) {
        radial.setInfoPane(
          new InfoPane(
            { header: c.info.title, body: c.info.summary },
            { fontFamily: opts.fontFamily },
          ),
        );
      }

      buildChildrenFromJSON(
        radial,
        resolvedChildren,
        {
          preview: opts.preview,
          layout: c.layout,
          fontFamily: opts.fontFamily,
          moduleTagging: opts.moduleTagging,
        },
        deps,
      );
    }
  }
}

function getNodeDisplayParameters({
  count,
  index: i,
  layout,
}: {
  count: number;
  index: number;
  layout?: RadialLayout;
}): { angle: number; stemLength: number; size: number } {
  let angle = (360 * (i + 0.25)) / count;
  let stemLength = Math.min(160 + 3 * count, 220);
  let size = Math.min(75 - 3 * count, 60);

  switch (layout ?? (count >= 10 ? 'star' : 'clock')) {
    case 'anchor':
      if (i === 0) {
        angle = 90;
        size = 60;
      } else {
        angle = i === 0 ? 90 : 360 - (180 * i) / count;
        size = 75 - 5 * count;
        stemLength = 200;
      }
      break;
    case 'arc':
      angle = 0 + (180 * i) / (count - 1);
      stemLength = 185 + 5 * count;
      size = 75 - 5 * count;
      break;
    case 'clock':
      angle = 90 + (360 * i) / count;
      break;
    case 'levels':
      angle = 10 + (360 * i) / (count + 1);
      size = 70 - 3 * count + i;
      stemLength = 170 + 4 * i;
      break;
    case 'modes':
      angle = 100 - 45 * (i + 1);
      size = 60;
      stemLength = 200;
      break;
    case 'operations':
      angle = 400 - 45 * (i + 1);
      size = 50;
      stemLength = 180;
      break;
    case 'skill-set':
      angle = 135 + 45 * i;
      size = 55 + 5 * i;
      stemLength = 160 + (i % 2 ? 0 : 40);
      break;
    case 'star':
      angle = 90 + (360 * i) / count;
      size = 70 - 2 * count;
      stemLength = i % 2 ? 160 : 230;
      break;
    case 'horizontal':
    case 'vertical': {
      const middleAngle = layout == 'vertical' ? 180 : 270;

      size = 75 - 5 * count;

      const dx = (600 - size * 3) * (-0.5 + i * (1 / (count - 1)));
      const dy = 200;

      angle = middleAngle + (Math.atan(dx / dy) * 180) / Math.PI;
      stemLength = Math.sqrt(dx * dx + dy * dy);

      break;
    }
  }

  return { angle, stemLength, size };
}
